/**
 * Money rules used across the app (same as the database view v_tender_summary):
 *   A bill is entered once it has been paid: one Invoice entry, status Paid, with its unique GR number.
 *   Paid          = Invoice entries marked Paid (plus older separate Payment entries marked Paid)
 *   Contract left = Contract value − Paid
 * Nothing is ever shown as negative; an excess is returned separately.
 */
export function outstandingOf(invoiced: number, paid: number): { outstanding: number; paidOverInvoiced: number } {
  const diff = invoiced - paid;
  return { outstanding: Math.max(0, diff), paidOverInvoiced: Math.max(0, -diff) };
}

export function contractLeftOf(contractValue: number, paid: number): { contractLeft: number; paidOverContract: number } {
  const diff = contractValue - paid;
  return { contractLeft: Math.max(0, diff), paidOverContract: Math.max(0, -diff) };
}

/** True when an entry counts towards "Paid". */
export function countsAsPaid(t: { txnType: string; status: string }): boolean {
  return (t.txnType === 'invoice' || t.txnType === 'payment') && t.status === 'paid';
}

/** Another (non-rejected) invoice on the same contract that already uses this GR/PO number, if any. */
export function findDuplicateInvoiceRef<T extends { id: string; txnType: string; status: string; grPoType?: string | null; grPoNumber?: string | null }>(
  existing: T[], selfId: string | undefined, grPoType: string, grPoNumber: string,
): T | undefined {
  const num = grPoNumber.trim().toLowerCase();
  if (!num) return undefined;
  return existing.find((x) => x.id !== selfId && x.txnType === 'invoice' && x.status !== 'rejected'
    && x.grPoType === grPoType && (x.grPoNumber ?? '').trim().toLowerCase() === num);
}

/**
 * GR/PO reference rule (same as the database trigger guard_financial_txn):
 * an Invoice that is not yet Paid may have no reference; a Paid invoice and every other entry type need both
 * GR/PO type and number; once a number has been entered it cannot be removed. Returns an error message or null.
 */
export function grPoRuleError(
  next: { txnType: string; status: string; grPoType?: string | null; grPoNumber?: string | null },
  previous?: { grPoType?: string | null; grPoNumber?: string | null },
): string | null {
  const num = next.grPoNumber?.trim() || null;
  const hadRef = !!(previous?.grPoType || previous?.grPoNumber?.trim());
  if (next.txnType === 'invoice' && next.status !== 'paid') {
    if (!num) return hadRef ? 'GR/PO reference cannot be removed once entered.' : null;
    return next.grPoType ? null : 'Choose GR or PO for the reference number.';
  }
  if (next.txnType === 'invoice') return next.grPoType && num ? null : 'Enter the GR/PO number to mark this invoice paid.';
  const isNew = !previous;
  if ((isNew || hadRef || next.grPoType || num) && (!next.grPoType || !num)) return 'GR/PO reference is required: choose GR or PO and enter its number.';
  return null;
}

/**
 * Bill period rule (same as the database trigger guard_bill_period): a paid bill (Invoice, Paid) needs
 * "period from" and "period to" when it is new or is being marked Paid; both or neither; from ≤ to;
 * once entered the period cannot be removed. Older paid bills without a period stay valid.
 */
export function billPeriodRuleError(
  next: { txnType: string; status: string; billPeriodFrom?: string | null; billPeriodTo?: string | null },
  previous?: { status: string; billPeriodTo?: string | null },
): string | null {
  const from = next.billPeriodFrom || null;
  const to = next.billPeriodTo || null;
  if (!!from !== !!to) return 'Enter both the bill period from and to dates.';
  if (from && to && from > to) return 'Bill period "from" must be on or before "to".';
  if (previous?.billPeriodTo && !to) return 'The bill period cannot be removed once entered.';
  const becomesPaid = next.txnType === 'invoice' && next.status === 'paid' && (!previous || previous.status !== 'paid');
  if (becomesPaid && !to) return 'Enter the bill period (from and to) for this paid bill.';
  return null;
}
